'use client'

import React, { useState, useRef, useCallback, useEffect } from 'react'

import type { CarouselBlock as CarouselBlockProps } from '@/payload-types'
import { BlockContainer } from '@/components/BlockContainer'
import { Media } from '@/components/Media'
import { CTAButton } from '@/components/ui/cta-button'
import { ArrowButton } from '@/components/ui/arrow-button'
import { resolveHref } from '@/utilities/resolveHref'
import { mapLegacyAppearance } from '@/fields/link'

type Props = CarouselBlockProps & {
  disableInnerContainer?: boolean
  reverseLayout?: boolean
}

export const TextToLeftCarousel: React.FC<Props> = ({
  slides = [],
  disableInnerContainer,
  enableAutoPlay = false,
  autoPlayDelay = 5000,
  reverseLayout = false,
  paddingTop,
  paddingBottom,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const currentSlideRef = useRef(0)
  const previousSlideRef = useRef<number | null>(null)
  const [progressKey, setProgressKey] = useState(0)
  const [slideDimensions, setSlideDimensions] = useState({ width: 239, height: 217 })
  const [isPaused, setIsPaused] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const navigationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const startNavigationDebounce = useCallback(() => {
    setIsNavigating(true)
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current)
    }
    navigationTimerRef.current = setTimeout(() => {
      setIsNavigating(false)
      navigationTimerRef.current = null
    }, 150)
  }, [])

  const transitionToSlide = useCallback(
    (index: number | ((prev: number) => number)) => {
      setCurrentSlide((prev) => {
        previousSlideRef.current = prev
        const next = typeof index === 'function' ? index(prev) : index
        currentSlideRef.current = next
        return next
      })
      setProgressKey((k) => k + 1)
    },
    [],
  )

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current && slides) {
      const scrollLeft = scrollContainerRef.current.scrollLeft
      const slideWidth = slideDimensions.width + 12
      const newSlide = Math.round(scrollLeft / slideWidth)
      if (newSlide !== currentSlide && newSlide >= 0 && newSlide < slides.length) {
        startNavigationDebounce()
        transitionToSlide(newSlide)
      }
    }
  }, [currentSlide, slides, slideDimensions.width, startNavigationDebounce, transitionToSlide])

  const calculateSlideDimensions = useCallback(() => {
    const calcContainerWidth =
      window.innerWidth >= 425 && window.innerWidth < 768
        ? window.innerWidth - 56
        : Math.min(window.innerWidth - 56, 375)

    const slidePercentage = window.innerWidth < 425 ? 0.75 : 0.7
    const slideWidth = Math.floor(calcContainerWidth * slidePercentage)
    const slideHeight = Math.floor(slideWidth * (280 / 307))

    setSlideDimensions({ width: slideWidth, height: slideHeight })
    setIsMobile(window.innerWidth < 768)
  }, [])

  // Navigate to a specific slide (for mobile dot indicators)
  const goToSlide = useCallback(
    (index: number) => {
      if (scrollContainerRef.current && slides && index >= 0 && index < slides.length) {
        const slideWidth = slideDimensions.width + 12
        const targetScrollLeft = index * slideWidth

        scrollContainerRef.current.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth',
        })

        transitionToSlide(index)
      }
    },
    [slides, slideDimensions.width, transitionToSlide],
  )

  const goToNext = useCallback(() => {
    if (!slides || slides.length === 0) return

    if (isMobile) {
      const nextIndex = (currentSlideRef.current + 1) % slides.length
      goToSlide(nextIndex)
      return
    }

    transitionToSlide((prev) => (prev + 1) % slides.length)
  }, [slides, isMobile, transitionToSlide, goToSlide])

  const goToPrevious = useCallback(() => {
    if (!slides || slides.length === 0) return

    if (isMobile) {
      const prevIndex = (currentSlideRef.current - 1 + slides.length) % slides.length
      goToSlide(prevIndex)
      return
    }

    transitionToSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }, [slides, isMobile, transitionToSlide, goToSlide])

  useEffect(() => {
    calculateSlideDimensions()
    const handleResize = () => calculateSlideDimensions()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [calculateSlideDimensions])

  useEffect(() => {
    return () => {
      if (navigationTimerRef.current) clearTimeout(navigationTimerRef.current)
    }
  }, [])

  // Auto-play: triggered by progress bar animationend event (desktop/tablet)
  // Falls back to setInterval for mobile where progress bar isn't rendered
  useEffect(() => {
    if (!enableAutoPlay || isPaused || !slides || slides.length <= 1) {
      return
    }

    // Only use interval for mobile - desktop/tablet uses onAnimationEnd
    if (!isMobile) return

    const interval = setInterval(() => {
      goToNext()
    }, autoPlayDelay ?? 5000)

    return () => clearInterval(interval)
  }, [enableAutoPlay, isPaused, autoPlayDelay, slides, isMobile, goToNext])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        startNavigationDebounce()
        goToPrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        startNavigationDebounce()
        goToNext()
      }
    },
    [goToNext, goToPrevious, startNavigationDebounce],
  )

  const handleProgressComplete = useCallback(() => {
    if (!slides || slides.length <= 1) return
    transitionToSlide((prev) => (prev + 1) % slides.length)
  }, [slides, transitionToSlide])

  const handleFocusIn = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsPaused(true)
  }, [])

  const handleFocusOut = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsPaused(false)
  }, [])

  if (!slides || slides.length === 0) {
    return null
  }

  const currentSlideData = slides[currentSlide]
  const previousSlideData =
    previousSlideRef.current !== null ? slides[previousSlideRef.current] : null
  const ctaHref = currentSlideData?.links?.[0]?.link
    ? resolveHref(currentSlideData.links[0].link)
    : null
  const ctaNewTab = currentSlideData?.links?.[0]?.link?.newTab
  const ctaLabel = currentSlideData?.links?.[0]?.link?.label || 'View slide'

  return (
    <BlockContainer
      disableInnerContainer={disableInnerContainer}
      className="focus:outline-none focus-visible:ring-2 focus-visible:ring-civo-lime focus-visible:ring-offset-2"
      paddingTop={paddingTop ?? true}
      paddingBottom={paddingBottom ?? true}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-roledescription="carousel"
      aria-label="Image carousel"
    >
      {/* Main Layout */}
      <div
        className={`flex flex-col gap-7 md:flex-row md:gap-5 ${reverseLayout ? 'md:flex-row-reverse' : ''}`}
      >
        {/* Text Content - Current Slide */}
        <div
          className="flex flex-col gap-4 w-full md:w-1/2 md:shrink-0 lg:w-[40%] md:justify-between md:border md:border-civo-cloud md:rounded-[16px] md:p-[22px] lg:p-7 md:h-[416px]"
          aria-live="polite"
          aria-atomic="true"
        >
          <div
            key={currentSlide}
            className="flex flex-col gap-9 animate-fadeIn motion-reduce:animate-none"
          >
            <div className="flex flex-col gap-3 text-black">
              <h3 className="subheading-medium lg:subheading-xlarge mb-0">{currentSlideData?.title}</h3>
              <p className="body-copy-medium lg:body-large text-black/70 mb-0">{currentSlideData?.description}</p>
            </div>

            {/* CTA Button - Only visible on desktop/tablet */}
            {currentSlideData?.links?.[0]?.link && (
              <div className="hidden md:block">
                <CTAButton
                  variant={mapLegacyAppearance(currentSlideData.links[0].link.appearance)}
                  href={ctaHref || undefined}
                >
                  {currentSlideData.links[0].link.label}
                </CTAButton>
              </div>
            )}
          </div>

          {/* Desktop/Tablet Navigation Controls */}
          {slides.length > 1 && (
            <div className="hidden md:flex gap-2 justify-end">
              <ArrowButton
                direction="left"
                backgroundColor="lime"
                size="medium"
                variant="minimal"
                animated
                onClick={() => {
                  startNavigationDebounce()
                  goToPrevious()
                }}
                aria-label="Previous slide"
              />
              <ArrowButton
                direction="right"
                backgroundColor="lime"
                size="medium"
                variant="minimal"
                animated
                onClick={() => {
                  startNavigationDebounce()
                  goToNext()
                }}
                aria-label="Next slide"
              />
            </div>
          )}
        </div>

        {/* Image Container */}
        <div
          ref={scrollContainerRef}
          className="w-[calc(100vw-56px)] max-w-[375px] overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide md:w-auto md:max-w-none md:flex-1 md:overflow-visible"
          onScroll={handleScroll}
        >
          {/* Mobile: Horizontal scroll layout */}
          <div className="flex gap-3 md:hidden">
            {slides.map((slide, index) => (
              <div
                key={slide.id || index}
                className="relative rounded-[8px] overflow-hidden shrink-0 snap-start max-h-[416px]"
                style={{
                  width: `${slideDimensions.width}px`,
                  height: `${slideDimensions.height}px`,
                }}
              >
                {slide?.image && (
                  <Media
                    resource={slide.image}
                    className="w-full h-full object-cover"
                    imgClassName="w-full h-full object-cover"
                  />
                )}
                {slide?.links?.[0]?.link && (
                  <div className="absolute top-[22px] right-[22px]">
                    <ArrowButton
                      backgroundColor="lime"
                      size="large"
                      direction="right"
                      className="-rotate-45"
                      onClick={() => {
                        const href = resolveHref(slide.links?.[0]?.link)
                        if (href) {
                          if (href.startsWith('#')) {
                            const element = document.getElementById(href.slice(1))
                            element?.scrollIntoView({ behavior: 'smooth' })
                          } else if (slide.links?.[0]?.link?.newTab) {
                            window.open(href, '_blank', 'noopener,noreferrer')
                          } else {
                            window.location.href = href
                          }
                        }
                      }}
                      aria-label={slide.links?.[0]?.link.label || 'View more'}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Tablet/Desktop: Single image with crossfade and progress bar */}
          <div className="hidden md:block relative h-[416px] rounded-[16px] overflow-hidden">
            {/* Previous image - static layer underneath for smooth crossfade */}
            {previousSlideData?.image && (
              <div className="absolute inset-0">
                <Media
                  resource={previousSlideData.image}
                  className={`w-full h-full object-cover ${previousSlideData.showInColour ? '' : 'grayscale'}`}
                  imgClassName="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Current image - fades in on top */}
            <div className="absolute inset-0">
              {currentSlideData?.image && (
                <Media
                  key={currentSlide}
                  resource={currentSlideData.image}
                  className={`w-full h-full object-cover animate-fadeIn motion-reduce:animate-none ${currentSlideData.showInColour ? '' : 'grayscale'}`}
                  imgClassName="w-full h-full object-cover"
                />
              )}
              {ctaHref && (
                <a
                  href={ctaHref}
                  target={ctaNewTab ? '_blank' : undefined}
                  rel={ctaNewTab ? 'noopener noreferrer' : undefined}
                  aria-label={ctaLabel}
                  className="absolute inset-0"
                />
              )}
            </div>

            {/* Progress bar */}
            {enableAutoPlay && slides.length > 1 && (
              <div
                className="absolute bottom-0 left-0 right-0 h-1.5 rounded-b-[16px] overflow-hidden"
                aria-hidden="true"
              >
                {!isNavigating && (
                  <div
                    key={progressKey}
                    className="h-full bg-civo-lime"
                    onAnimationEnd={handleProgressComplete}
                    style={{
                      animation: `progressFill ${autoPlayDelay ?? 5000}ms linear forwards`,
                      animationPlayState: isPaused ? 'paused' : 'running',
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Position Indicators */}
        {slides.length > 1 && (
          <div className="flex gap-1 md:hidden">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`flex-1 h-1 rounded-full transition-colors duration-200 ${index === currentSlide ? 'bg-black' : 'bg-civo-cloud'}`}
                onClick={() => {
                  startNavigationDebounce()
                  goToSlide(index)
                }}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={index === currentSlide ? true : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </BlockContainer>
  )
}
